import { Component } from 'react';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { Logger } from '../../../models/logger';
import { LightHttp } from '../../../services/light-http.service';
import { UIContext } from '../../../services/ui-context';
import { HtmlEntityTypes } from '../../../utils/icon.utils';
import { Rating } from '../../../utils/request.utils';
import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import styles from './rating-display.component.module.scss';
import { ApiConfig } from '../../../services/api-config';
import { OptionalChildrenProps } from '../../../types/react.types';


interface RatingDisplayComponentProps extends OptionalChildrenProps {
    description: string;
    title: string;
    feature: string;
    rating: Rating;
}


export default class RatingDisplayComponent extends Component<RatingDisplayComponentProps> {
    private readonly logger = Logger.getLogger('RatingDisplayComponent');

    readableFeatureName: string;
    totalRatings: number;
    currentRating: number;
    currentRatingStr: string;
    jsonSchema;

    @lazyInject(IOC_TYPES.LightHttp) private http: LightHttp;
    @lazyInject(IOC_TYPES.UIContext) private uiContext: UIContext;
    @lazyInject(IOC_TYPES.ApiConfig) private apiConfig: ApiConfig;

    constructor(props) {
        super(props);
        this.updateRatings(this.props.rating);
        this.addRatingScript(this.props.rating);
    }

    updateRatings(ratingInfo: Rating) {
        this.readableFeatureName = this.props.feature.replace(/-/g, ' ');
        if (ratingInfo) {
            this.totalRatings =
                ((ratingInfo.rating_1 || 0) +
                    (ratingInfo.rating_2 || 0) +
                    (ratingInfo.rating_3 || 0) +
                    (ratingInfo.rating_4 || 0) +
                    (ratingInfo.rating_5 || 0)) || 1;
            this.currentRating = ((ratingInfo.rating_1 || 0) + 2 * (ratingInfo.rating_2 || 0) +
                3 * (ratingInfo.rating_3) + 4 * (ratingInfo.rating_4) +
                5 * (ratingInfo.rating_5)) / this.totalRatings;
            this.currentRating = parseFloat(Number(this.currentRating).toFixed(1));
            this.currentRatingStr = Number(this.currentRating).toFixed(1);
        }
    }

    getRatingStars() {
        const stars = [];
        for (let i = 0; i < 5; i++) {
            if (this.currentRating >= i) {
                if (this.currentRating < i + 1) {
                    stars.push('half');
                } else {
                    stars.push('full');
                }
            } else {
                stars.push('empty');
            }
        }
        return <div className={`${styles['stars']}`}>
            {stars.map((v, i) => {
                return <span key={`star-${i}`}
                    onClick={(e) => this.rateFeature(i + 1)}
                    dangerouslySetInnerHTML={{ __html: v === 'full' ? HtmlEntityTypes.STAR : (v === 'half' ? HtmlEntityTypes.HALF_START : HtmlEntityTypes.NO_STAR) }}>
                </span>
            })
            }
        </div>
    }

    addRatingScript(ratingInfo: Rating) {
        if (ratingInfo) {
            const json = {
                '@context': 'http://schema.org',
                '@type': 'Product',
                'brand': 'XConvert',
                'name': this.props.title || this.props.feature,
                'description': this.props.description,
                'category': 'Productivity',
                'aggregateRating': {
                    '@type': 'AggregateRating',
                    'worstRating': '1',
                    'bestRating': '5',
                    'ratingValue': this.currentRating,
                    'ratingCount': this.totalRatings,
                },
            };
            this.jsonSchema = JSON.stringify(json);
        }
    }

    rateFeature(rating: number) {
        this.http.put(`${this.apiConfig.getHttpsEndpoint()}/comm/rating/${this.props.feature}`, {
            value: rating,
        }).subscribe({
            next: (res: any) => {
                if (res.rated) {
                    this.uiContext.successToast('Thank you for rating!');
                    setTimeout(() => {
                        this.loadRatings$().subscribe();
                        this.forceUpdate();
                    }, 500);
                } else {
                    this.uiContext.warnToast('You can only rate a feature once', '', false, false);
                }
            },
            error: err => {
                this.logger.error({ error: err, message: `Error rating feature ${this.props.feature} to ${rating}.` });
            },
        }
        );
    }

    loadRatings$() {
        return this.http.get<Rating>(`${this.apiConfig.getHttpsEndpoint()}/comm/rating/${this.props.feature}`).pipe(
            tap((data: Rating) => this.updateRatings(data)),
            catchError(err => {
                this.logger.error({ error: err, message: `Error getting rating.` });
                return of({});
            }),
        );
    }

    render() {
        return <div className="section" >
            <div id="schemaScript" dangerouslySetInnerHTML={
                { __html: '<script type=\'application/ld+json\'>' + this.jsonSchema + '</script>' }
            }>
            </div>
            <div>
                <h4>Rate {this.props.title} Tool</h4>
                <div>
                    {this.getRatingStars()}
                    <span className="text ms-3">Rating: {this.currentRatingStr} / 5 - {this.totalRatings} reviews</span>
                </div>
            </div >

        </div >
    }
}